import { OnboardingTour } from "./OnboardingTour"
import {
	type ContextType,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { useWorkspace } from "~/components/workspace/Workspace"
import { authClient } from "~/lib/auth-client"
import { tutorials } from "~/lib/tutorials"
import { api } from "~/trpc/react"

import { TutorialContext } from "./context"

export function TutorialProvider({ children }: { children: ReactNode }) {
	const { data: session } = authClient.useSession()
	const { orgSlug } = useParams()
	const workspace = useWorkspace()
	const location = useLocation()
	const navigate = useNavigate()
	const currentPath = useRef(location.pathname)
	currentPath.current = location.pathname
	const utils = api.useUtils()
	const { client } = utils
	const [gate, setGate] = useState({ ready: false, allowAutomatic: false })
	const [pending, setPending] =
		useState<ContextType<typeof TutorialContext>["pending"]>()
	const [announcement, setAnnouncement] =
		useState<ContextType<typeof TutorialContext>["announcement"]>()
	const [busy, setBusy] = useState(false)
	const [onboardingSession, setOnboardingSession] = useState<string>()
	const onboardingEntered = useRef(false)
	useEffect(() => {
		if (!onboardingSession) {
			onboardingEntered.current = false
			return
		}
		if (location.pathname === `/${orgSlug}/me/onboarding`)
			onboardingEntered.current = true
		else if (onboardingEntered.current) setOnboardingSession(undefined)
	}, [location.pathname, orgSlug, onboardingSession])
	useEffect(() => {
		setOnboardingSession(undefined)
	}, [session?.user.id, orgSlug])
	const [blocked, setBlocked] = useState(false)
	useEffect(() => {
		if (
			gate.ready &&
			!onboardingSession &&
			location.pathname === `/${orgSlug}/me/onboarding`
		) {
			navigate(`/${orgSlug}/me/issues`, { replace: true })
		}
	}, [gate.ready, onboardingSession, location.pathname, orgSlug, navigate])
	const isMobile = workspace?.isMobile ?? false
	const owner = useRef<symbol | undefined>(undefined)
	const routedRequest = useRef<typeof pending>(undefined)
	const sessionKey = session
		? `orbit-tutorial-login:onboarding-v1:${session.user.id}:${session.session.id}`
		: undefined

	useEffect(() => {
		if (!sessionKey) return
		if (currentPath.current.endsWith("/me/onboarding")) {
			setGate({ ready: true, allowAutomatic: false })
			return
		}
		let active = true
		setGate({ ready: false, allowAutomatic: false })
		setPending(undefined)
		setAnnouncement(undefined)
		void (async () => {
			try {
				let handled: string | null = null
				try {
					handled = sessionStorage.getItem(sessionKey)
				} catch {
					/* Storage is optional. */
				}
				if (handled) {
					setGate({ ready: true, allowAutomatic: handled === "none" })
					return
				}
				const preferences = await client.user.getTutorialPreferences.query()
				if (!active) return
				const prompt = preferences.loginPrompt
				if (prompt && tutorials[prompt.id].desktopOnly && isMobile) {
					setGate({ ready: true, allowAutomatic: false })
					return
				}
				// One login experience, including reloads and organization switches.
				// Finishing welcome must not immediately unleash a release invitation.
				try {
					sessionStorage.setItem(sessionKey, prompt?.kind ?? "none")
				} catch {
					/* Storage is optional. */
				}
				setBlocked(
					!!document.querySelector(
						'[data-slot="dialog-content"]:not([data-onboarding]), [role="alertdialog"]'
					)
				)
				if (prompt?.kind === "welcome")
					setPending({ id: prompt.id, source: "welcome" })
				else if (prompt) setAnnouncement(prompt.id)
				setGate({
					ready: true,
					allowAutomatic: !prompt && !preferences.globalHideTutorials
				})
			} catch {
				// Fail closed: a failed preference lookup must never opt someone in.
				if (active) setGate({ ready: true, allowAutomatic: false })
			}
		})()
		return () => {
			active = false
		}
	}, [client, isMobile, sessionKey])

	useEffect(() => {
		if (!pending && !announcement) {
			setBlocked(false)
			return
		}
		const update = () =>
			setBlocked(
				!document.querySelector("[data-onboarding]") &&
					!!document.querySelector(
						'[data-slot="dialog-content"]:not([data-onboarding]), [role="alertdialog"]'
					)
			)
		update()
		const observer = new MutationObserver(update)
		observer.observe(document.body, { childList: true, subtree: true })
		return () => observer.disconnect()
	}, [announcement, pending])

	useEffect(() => {
		if (!pending || !orgSlug || routedRequest.current === pending) return
		routedRequest.current = pending
		const tutorial = tutorials[pending.id]
		const destination =
			pending.destination ??
			(tutorial.destination ? `/${orgSlug}${tutorial.destination}` : undefined)
		if (!destination) return
		if (
			`${location.pathname}${location.search}${location.hash}` === destination
		)
			return
		// Open a desktop workspace tab instead of navigating away from a draft.
		if (workspace && !workspace.isMobile) workspace.open(destination)
		else void navigate(destination)
	}, [
		location.hash,
		location.pathname,
		location.search,
		navigate,
		orgSlug,
		pending,
		workspace
	])

	useEffect(() => {
		if (!pending || pending.id === "onboarding-v1" || busy || blocked) return
		const timeout = window.setTimeout(() => {
			setPending(undefined)
			toast.error("Couldn't open this tutorial. Try its feature's help button.")
		}, 10000)
		return () => window.clearTimeout(timeout)
	}, [blocked, busy, pending])

	const claim = useCallback((next: symbol) => {
		if (owner.current) return false
		owner.current = next
		setBusy(true)
		return true
	}, [])
	const release = useCallback((current: symbol) => {
		if (owner.current !== current) return
		owner.current = undefined
		setBusy(false)
		setPending(undefined)
	}, [])

	return (
		<TutorialContext.Provider
			value={{
				onboardingSession,
				beginOnboarding: () => {
					if (pending?.id !== "onboarding-v1" || !session || !orgSlug)
						return false
					setOnboardingSession(crypto.randomUUID())
					return true
				},
				endOnboarding: () => setOnboardingSession(undefined),
				resetDiscovery: () => {
					// Restore hints now; offer the welcome again on next load,
					// without interrupting the settings screen with a tour.
					if (sessionKey) {
						try {
							sessionStorage.removeItem(sessionKey)
						} catch {
							/* Storage is optional. */
						}
					}
					setAnnouncement(undefined)
					setGate({ ready: true, allowAutomatic: true })
				},
				...gate,
				busy:
					busy ||
					pending?.id === "onboarding-v1" ||
					location.pathname.endsWith("/me/onboarding"),
				blocked,
				pending,
				announcement,
				claim,
				release,
				closeAnnouncement: (id) => {
					setAnnouncement((current) => (current === id ? undefined : current))
				},
				start: (id, source = "manual", destination) => {
					if (!gate.ready || owner.current || pending) return false
					setAnnouncement(undefined)
					setPending({ id, source, destination })
					return true
				},
				acknowledgeAnnouncement: async (id, outcome) => {
					await client.user.finishTutorial.mutate({
						tutorialId: id,
						kind: "announcement",
						outcome
					})
					setAnnouncement((current) => (current === id ? undefined : current))
					await utils.user.getTutorialPreferences.invalidate()
				}
			}}
		>
			{children}
			{pending?.id === "onboarding-v1" && !blocked && (
				<OnboardingTour
					key={session?.user.id}
					onClose={() => setPending(undefined)}
				/>
			)}
		</TutorialContext.Provider>
	)
}
